import React from 'react';
import { ArrowRight, Check, X } from 'lucide-react';

interface ClassificationSuggestion {
  suggested_changes: Record<string, string>;
  current_classification: Record<string, string>;
  reasoning: string;
  confidence: number;
  requires_approval: boolean;
  document_id: string;
}

interface ClassificationSuggestionCardProps {
  suggestion: ClassificationSuggestion;
  onAccept: (suggestion: ClassificationSuggestion) => void;
  onReject: (suggestion: ClassificationSuggestion) => void;
  isProcessing?: boolean;
}

/**
 * ClassificationSuggestionCard - CCE-007
 * Displays Emma's classification correction suggestions with accept/reject actions.
 * Appears inline in Emma chat when she suggests classification changes.
 */
export function ClassificationSuggestionCard({
  suggestion,
  onAccept,
  onReject,
  isProcessing = false
}: ClassificationSuggestionCardProps) {
  const confidencePercent = Math.round(suggestion.confidence * 100);
  const confidenceColor = confidencePercent >= 80 ? 'text-green-600' : confidencePercent >= 50 ? 'text-yellow-600' : 'text-red-600';

  return (
    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
      <h4 className="font-medium mb-3 text-blue-900 dark:text-blue-100">Suggested Changes:</h4>
      
      <div className="space-y-2 mb-4">
        {Object.entries(suggestion.suggested_changes).map(([facet, newValue]) => {
          const oldValue = suggestion.current_classification[facet] || '(none)';
          return (
            <div key={facet} className="flex items-center text-sm">
              <span className="inline-flex items-center px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                {oldValue}
              </span>
              <ArrowRight className="inline mx-2 h-4 w-4 text-gray-500" />
              <span className="inline-flex items-center px-2 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 font-medium">
                {newValue}
              </span>
              <span className="ml-2 text-gray-500 text-xs">({facet})</span>
            </div>
          );
        })}
      </div>

      {suggestion.reasoning && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 italic">
          "{suggestion.reasoning}"
        </p>
      )}

      <div className="flex items-center justify-between">
        <span className={`text-xs font-medium ${confidenceColor}`}>
          {confidencePercent}% confidence
        </span>

        <div className="flex gap-2">
          <button
            onClick={() => onReject(suggestion)}
            disabled={isProcessing}
            className="inline-flex items-center px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <X className="h-4 w-4 mr-1" />
            Reject
          </button>
          <button
            onClick={() => onAccept(suggestion)}
            disabled={isProcessing}
            className="inline-flex items-center px-3 py-1.5 text-sm rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Check className="h-4 w-4 mr-1" />
            Accept & Flag for Review
          </button>
        </div>
      </div>

      {suggestion.requires_approval && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          ⚠️ This change will be flagged for human review before final approval.
        </p>
      )}
    </div>
  );
}
